import { Grid, MenuItem, Select, SelectChangeEvent, Tab, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import * as api from "api/Core";
import { DefaultListParameters } from "assets/appConfig";
import { AppMessageBox } from "components/AppMessageBox";
import { AppTabs } from "components/AppTabs";
import IconButtonCancel from "components/Buttons/IconButtonCancel";
import { SpinnerIndicator } from "components/CallServer";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import MuiReverse from "components/MuiExtras/Typography/MuiReverse";
import UserFullProfileLink from "components/UserFullProfileLink";
import UserProfileImageLink from "components/UserProfileImageLink";
import LibUtils from "lib/LibUtils";
import { DashboardGraphModel, DashboardUserAndGroupsParticipationAccessDetailColumn, DashboardUserAndGroupsParticipationSummaryColumn, DashboardUserModel, PagedResult } from "models/ApiModels";
import moment from "moment";
import * as React from "react";
import { WithTranslation } from "react-i18next";
import { RenderGraphGmtInfo } from "views/Dashboards/components/RenderGraphGmtInfo";
import { RenderLineGraph } from "views/Dashboards/components/RenderLineGraph";
import { IParticipationGraphProps, IParticipationListProps, ParticipationGraphPeriod, ParticipationListAccessPeriod } from "views/Dashboards/UserAndGroupsDashboard/Common";

/////////////////////////////////////////////////////////////////////////////////////

interface IGraphState {
    data: DashboardGraphModel[];
    loading: boolean;
    tabOption?: ParticipationGraphPeriod;
    selectedDay?: Date;
}

type Props = IParticipationGraphProps & WithTranslation;

export class ParticipationAccessGraph extends React.PureComponent<Props, IGraphState>{

    constructor(props: Props) {
        super(props);

        this.state = {
            data: null,
            loading: true,
            selectedDay: null,
            tabOption: null
        };
    }

    private loadData() {

        var de = moment().utc().endOf("day");
        var ds = moment().utc().startOf("day").add(-6, "days");

        api.getDashboardUserAndGroupsParticipationAccess({
            detailColumn: DashboardUserAndGroupsParticipationAccessDetailColumn.Graph,
            startDate: ds.toDate(),
            endDate: de.toDate(),
            range: null,
            ...this.props.filter
        })
            .then((result: DashboardGraphModel[]) => {
                result.forEach((v) => {
                    v.day = v.day.convertLocalToUtcDate();
                });

                this.setState({ data: result, loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false });
            });
    }

    public componentDidMount() {
        this.loadData();
    }

    ///
    public componentDidUpdate(prevProps: Props) {
        if (!LibUtils.isEqual(this.props.filter, prevProps.filter)) {
            this.loadData();
        }
    }

    public render() {
        const { tile } = this.props;
        const { loading, data } = this.state;

        if (loading)
            return <SpinnerIndicator />;

        if (data == null)
            return <AppMessageBox resource="LoadGraphDataFailed" />;

        return (
            <>
                <MuiContainer margin="dense" >
                    <Grid container justifyContent="space-between">
                        <Grid item>
                            <Typography component="p" variant="h6"><b>{this.props.t("Views.Dashboards.UserAndGroups.ParticipationAccess.Graph.HeaderTitle").toString().toLocaleUpperCase()}</b></Typography>
                            <MuiMuted>
                                <small>
                                    <RenderGraphGmtInfo />
                                </small>
                            </MuiMuted>
                        </Grid>

                        {
                            this.props.tile != DashboardUserAndGroupsParticipationSummaryColumn.Accesses &&
                            <Grid item>
                                <AppTabs
                                    value={this.state.tabOption}
                                    onChange={
                                        (e, newValue: ParticipationGraphPeriod) => {
                                            this.setState({ tabOption: newValue }, () => this.loadData());
                                        }
                                    }
                                    indicatorColor="primary"
                                    textColor="primary"
                                >
                                    <Tab label={this.props.t<string>("Views.Dashboards.Common.Days30")} value={ParticipationGraphPeriod.Last30} />
                                    <Tab label={this.props.t<string>("Views.Dashboards.Common.Months12")} value={ParticipationGraphPeriod.Last12} />
                                </AppTabs>
                            </Grid>
                        }
                    </Grid>
                </MuiContainer>

                <RenderLineGraph
                    color="orange"
                    data={this.state.data}
                    onSelectDay={(dt) => this.setState({ selectedDay: dt })} />

                <ParticipationAccessList
                    {...this.props}
                    tile={tile}
                    selectedDay={this.state.selectedDay}
                    graphPeriod={this.state.tabOption}
                    onCancelDay={() => this.setState({ selectedDay: null })}
                />
            </>
        );
    }
}

////////////////////////////////////////////////////////////////////////////////////////

interface IListState {
    dialog: any;
    data: PagedResult<DashboardUserModel>;
    offset: number;
    loading: boolean;
    listOption: DashboardUserAndGroupsParticipationAccessDetailColumn;
    tabOption: ParticipationListAccessPeriod;
}

type Listprops = IParticipationListProps & WithTranslation;

export class ParticipationAccessList extends React.PureComponent<Listprops, IListState> {

    constructor(props: Listprops) {
        super(props);

        this.state = {
            dialog: null,
            data: null,
            offset: null,
            loading: true,
            listOption: DashboardUserAndGroupsParticipationAccessDetailColumn.Access,
            tabOption: ParticipationListAccessPeriod.Last7
        };
    }

    ///
    private loadData(offset: number) {

        this.setState({ loading: true });

        api.getDashboardUserAndGroupsParticipationAccess(
            {
                detailColumn: this.state.listOption,
                range: { offset, take: DefaultListParameters.viewRecordsByPage },
                ...this.getDateFilter(),
                ...this.props.filter
            })
            .then((result) => {
                this.setState({ data: result, loading: false, offset: offset });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false, offset: 0 });
            });
    }

    ///
    private getDateFilter() {

        if (this.props.selectedDay) {
            return {
                startDate: this.props.selectedDay.convertUtcToLocalDate(),
                endDate: this.props.selectedDay.putEndOfDay().convertUtcToLocalDate()
            };
        }

        var lastDay = moment().utc().endOf("day");
        var firstDay = moment().utc().startOf("day");
        if (this.state.tabOption == ParticipationListAccessPeriod.Last7)
            firstDay.add(-6, "days");
        else
            firstDay.add(-29, "days");

        return {
            startDate: firstDay.toDate(),
            endDate: lastDay.toDate()
        };
    }

    ///
    private handleListOption = (e: SelectChangeEvent<DashboardUserAndGroupsParticipationAccessDetailColumn>) => {
        this.setState({ listOption: e.target.value as DashboardUserAndGroupsParticipationAccessDetailColumn}, () => this.loadData(0));
    };

    ///
    private renderTitle() {

        const sDay = this.props.selectedDay ? moment(this.props.selectedDay).format("DD/MMM") : null;

        return (
            <MuiContainer margin="dense" >
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item>

                        <Select
                            value={this.state.listOption}
                            onChange={this.handleListOption}
                        >
                            <MenuItem value={DashboardUserAndGroupsParticipationAccessDetailColumn.Access}>{this.props.t("Views.Dashboards.UserAndGroups.ParticipationAccess.List.MenuItem1")}</MenuItem>
                            <MenuItem value={DashboardUserAndGroupsParticipationAccessDetailColumn.NotAccess}>{this.props.t("Views.Dashboards.UserAndGroups.ParticipationAccess.List.MenuItem2")}</MenuItem>
                        </Select>

                    </Grid>
                    <Grid item>

                        {
                            this.props.selectedDay
                                ? (
                                    <MuiReverse color="warning">
                                        <b>{`Dia ${sDay}`}</b>
                                        <IconButtonCancel
                                            size="small"
                                            onClick={() => this.props.onCancelDay()}
                                        />
                                    </MuiReverse>
                                )
                                : (

                                    <AppTabs
                                        value={this.state.tabOption}
                                        onChange={
                                            (e, newValue: ParticipationListAccessPeriod) => {
                                                this.setState({ tabOption: newValue }, () => this.loadData(0));
                                            }
                                        }
                                        indicatorColor="primary"
                                        textColor="primary"
                                    >
                                        <Tab label={this.props.t<string>("Views.Dashboards.Common.LastWeek")} value={ParticipationListAccessPeriod.Last7} />
                                        <Tab label={this.props.t<string>("Views.Dashboards.Common.LastMonth")} value={ParticipationListAccessPeriod.Last30} />
                                    </AppTabs>
                                )
                        }

                    </Grid>
                </Grid>
            </MuiContainer>
        );
    }

    ///
    private renderList() {
        const { data: { items, total }, listOption, dialog, offset } = this.state;

        return (
            <MuiContainer margin="normal">
                <>
                    {dialog}
                    <br />
                    {this.renderTitle()}

                    {
                        this.props.selectedDay == null && total == 0
                            ? (
                                <MuiMessageBox variant="warning"
                                    message={
                                        this.props.t(
                                            listOption == DashboardUserAndGroupsParticipationAccessDetailColumn.Access
                                                ? "Views.Dashboards.UserAndGroups.ParticipationAccess.List.NobodyAccess"
                                                : "Views.Dashboards.UserAndGroups.ParticipationAccess.List.EveryoneAccess"
                                        )
                                    }
                                />
                            )
                            : total == 0
                                ? (
                                    <MuiMessageBox variant="warning"
                                        message={
                                            this.props.t(
                                                listOption == DashboardUserAndGroupsParticipationAccessDetailColumn.Access
                                                    ? "Views.Dashboards.UserAndGroups.ParticipationAccess.List.NobodyAccess"
                                                    : "Views.Dashboards.UserAndGroups.ParticipationAccess.List.EveryoneAccess"
                                            )
                                        }
                                    />
                                )
                                : (
                                    <>
                                        <MuiContainer>
                                            <Table size="small" >
                                                <TableBody>
                                                    {
                                                        items.map((v, index) => {
                                                            return (
                                                                <TableRow key={index}>
                                                                    <TableCell style={{ width: 1 }}>{offset + index + 1}</TableCell>

                                                                    <TableCell style={{ width: 1 }}>
                                                                        <UserProfileImageLink user={v} />
                                                                    </TableCell>

                                                                    <TableCell>
                                                                        <UserFullProfileLink color="secondary" user={v} />
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })
                                                    }
                                                </TableBody>
                                            </Table>
                                        </MuiContainer>
                                        <MuiPagination
                                            limit={DefaultListParameters.viewRecordsByPage}
                                            offset={this.state.offset}
                                            total={total}
                                            onClick={(e, offset) => this.loadData(offset)}
                                        />
                                    </>
                                )
                    }
                </>
            </MuiContainer >
        );
    }

    ///
    public componentDidMount() {
        this.loadData(0);
    }

    ///
    public componentDidUpdate(prevProps: Listprops) {
        if (
            this.props.filter != prevProps.filter
            ||
            (this.props.selectedDay && this.props.selectedDay != prevProps.selectedDay)
            ||
            (this.props.selectedDay == null && prevProps.selectedDay != null) || this.props.tile != prevProps.tile
        ) {
            this.loadData(0);
        }
    }

    ///
    public render() {

        if (this.state.loading)
            return <SpinnerIndicator />;

        if (this.state.data == null)
            return <AppMessageBox resource="LoadListDataFailed" />;

        return this.renderList();
    }
}